import { useState, useEffect, useRef } from "react";
import { InstagramLogo, Play, X, SpeakerHigh, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, ChapterLabel } from "./Reveal";
import { site } from "../content";

const ReelCard = ({ reel, index, onOpen }) => (
  <Reveal delay={0.06 * index}>
    <button
      type="button"
      onClick={() => onOpen(index)}
      aria-label="Avaa video"
      className="group relative block aspect-[9/16] w-full overflow-hidden rounded-3xl bg-[#2B2724] shadow-[0_8px_30px_rgba(43,39,36,0.06)] transition-[box-shadow,transform] duration-300 hover:shadow-[0_20px_50px_rgba(43,39,36,0.12)] hover:-translate-y-1 cursor-pointer"
      data-testid={`reel-card-${index + 1}`}
    >
      {reel.video ? (
        <video
          src={reel.video}
          poster={reel.poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid={`reel-video-${index + 1}`}
        />
      ) : (
        <img
          src={reel.poster}
          alt={reel.caption || "Työnäyte"}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/15 transition-colors duration-300">
        <span className="w-14 h-14 rounded-full bg-[#FBF8F4]/85 backdrop-blur-sm flex items-center justify-center shadow-lg opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-[opacity,transform] duration-300">
          <Play size={22} weight="fill" className="text-[#2B2724] ml-0.5" />
        </span>
      </span>
      {reel.caption ? (
        <>
          <span className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 to-transparent" />
          <span className="absolute bottom-4 left-4 right-4 text-sm font-medium text-[#FBF8F4]">
            {reel.caption}
          </span>
        </>
      ) : null}
    </button>
  </Reveal>
);

const Lightbox = ({ reel, onClose, onPrev, onNext }) => {
  const videoRef = useRef(null);

  const applyVolume = () => {
    if (videoRef.current) videoRef.current.volume = 0.25;
  };

  useEffect(() => {
    applyVolume();
  }, [reel.video]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onClose}
      data-testid="reel-lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Sulje"
        className="absolute top-5 right-5 w-11 h-11 rounded-full bg-[#FBF8F4]/10 hover:bg-[#FBF8F4]/20 text-[#FBF8F4] flex items-center justify-center transition-colors duration-200 z-10"
        data-testid="reel-lightbox-close"
      >
        <X size={22} />
      </button>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        aria-label="Edellinen video"
        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#FBF8F4]/10 hover:bg-[#FBF8F4]/20 text-[#FBF8F4] flex items-center justify-center transition-colors duration-200 z-10"
        data-testid="reel-lightbox-prev"
      >
        <CaretLeft size={24} />
      </button>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        aria-label="Seuraava video"
        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#FBF8F4]/10 hover:bg-[#FBF8F4]/20 text-[#FBF8F4] flex items-center justify-center transition-colors duration-200 z-10"
        data-testid="reel-lightbox-next"
      >
        <CaretRight size={24} />
      </button>

      <motion.div
        className="relative"
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <video
          key={reel.video}
          ref={videoRef}
          src={reel.video}
          poster={reel.poster}
          autoPlay
          loop
          playsInline
          controls
          onLoadedMetadata={applyVolume}
          onPlay={applyVolume}
          className="h-[80vh] max-h-[80vh] w-auto max-w-[92vw] rounded-2xl bg-black shadow-2xl"
          data-testid="reel-lightbox-video"
        />
        <p className="mt-3 flex items-center justify-center gap-2 text-sm text-[#FBF8F4]/70">
          <SpeakerHigh size={16} /> Ääni on päällä
        </p>
      </motion.div>
    </motion.div>
  );
};

export const InstagramSection = () => {
  const ig = site.some;
  const [openIndex, setOpenIndex] = useState(null);
  const activeReel = openIndex !== null ? ig.reels[openIndex] : null;
  const total = ig.reels.length;
  const goPrev = () => setOpenIndex((i) => (i - 1 + total) % total);
  const goNext = () => setOpenIndex((i) => (i + 1) % total);

  return (
    <section id="some" className="py-20 md:py-28 bg-[#EFE6DC]" data-testid="instagram-section">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <ChapterLabel chapter={ig.chapter} label={ig.label} />
          <div className="mt-5 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-serif font-light text-3xl md:text-5xl tracking-tight text-[#2B2724] max-w-xl">
              {ig.title}
            </h2>
            <a
              href={site.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full border-2 border-[#2B2724] text-[#2B2724] font-medium px-7 py-3 flex items-center gap-2 hover:bg-[#2B2724] hover:text-[#FBF8F4] transition-colors duration-200"
              data-testid="instagram-follow-button"
            >
              <InstagramLogo size={19} /> {ig.followCta}
            </a>
          </div>
          <p className="mt-5 text-base md:text-lg text-[#5C564F] max-w-xl">{ig.lead}</p>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
          {ig.reels.map((reel, i) => (
            <ReelCard key={i} reel={reel} index={i} onOpen={setOpenIndex} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {activeReel ? (
          <Lightbox
            reel={activeReel}
            onClose={() => setOpenIndex(null)}
            onPrev={goPrev}
            onNext={goNext}
          />
        ) : null}
      </AnimatePresence>
    </section>
  );
};
